import { useAuth } from '@teko/auth-react';
import { Body, Caption, Col, CustomLink, Flex, Shape } from '@teko/ds-desktop';
import {
  ChecklistOutlined,
  DownOutlined,
  LocationOutlined,
  OrderOutlined,
} from '@teko/ds-icon';
import { getTrackingData } from '@teko/tracking';
import { RegionPickerSuggestionPopover } from 'components/RegionPickerSuggestionPopover';
import {
  PopupRegionPicker,
  UserProfileHeader,
} from 'components/desktop/organisms';
import { NavigationButton } from 'components/desktop/organisms/style';
import {
  useFeatureFlags,
  useLocation,
  useThreeLevelLocationContext,
} from 'contexts';
import { getNavItems, isServerless } from 'helpers';
import { useLoyaltyMemberInfo, useRegionPicker, useTenantConfig } from 'hooks';
import { t } from 'i18n';
import { CartOverview } from 'modules/cart';
import { NotificationOverview } from 'modules/notification';
import { SlotComponentProps } from 'modules/page-builder';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import {
  NavItem,
  defaultNavItemDisplayConfig,
  isVerticalNavItem,
} from 'tenants/_template/desktop/_module/components/NavigationBar';

type HeaderRightItemProps = SlotComponentProps<{}>;

export const HeaderRightItemDesktop: React.FC<HeaderRightItemProps> = ({}) => {
  const {} = useRouter();
  const {
    partner,
    tenant,
    isDmsFlow,
    epicOmni1541,
    notificationAppId,
    epicOmni1538,
    notFetchLoyaltyMemberInfoGlobally,
    displayNewLayout,
  } = useTenantConfig();
  const { user, authService } = useAuth();
  const { loyaltyMemberInfo } = useLoyaltyMemberInfo(
    !notFetchLoyaltyMemberInfoGlobally,
  );
  const { flags } = useFeatureFlags();
  const regionPickerProps = useRegionPicker();
  const region = regionPickerProps?.activeLocation;

  const {
    setShowed: showLocationPopup,
    selectedLocation,
    showRegionPickerSuggestionPopover,
    onCloseRegionPickerSuggestionPopover,
    enabled: locationPickerEnabled,
  } = useLocation();
  const { correctThreeLevelLocation, onOpenThreeLevelLocationPopup } =
    useThreeLevelLocationContext();
  const onLogin = () => authService.login(window.location.href);
  const router = useRouter();

  const profileLinks = useMemo(() => {
    return getNavItems({
      tenant,
      logged: !!user?.userId,
      showLoyaltyItem:
        (notFetchLoyaltyMemberInfoGlobally
          ? !!user?.loyaltyMemberId
          : !!loyaltyMemberInfo) || !!epicOmni1538,
      showNotificationItem: !!notificationAppId,
      epicOmni1546: true,
      displayNewLayout: displayNewLayout,
    });
  }, [tenant, user, loyaltyMemberInfo]);

  const RegionPickerIcon = ({ locationName }: { locationName?: string }) => {
    const showRegionPickerModal = () => {
      if (flags.epic_ecom_284) {
        showLocationPopup(true);
      } else {
        regionPickerProps.onOpenPopup();
      }
    };

    // Don't show this icon on the server side
    if (!isServerless()) {
      return null;
    }

    return (
      <RegionPickerSuggestionPopover
        active={showRegionPickerSuggestionPopover}
        onClose={onCloseRegionPickerSuggestionPopover}
      >
        <NavItem
          onClick={showRegionPickerModal}
          {...getTrackingData({
            isTrackContent: true,
            regionName: 'headerBar',
            contentName: 'location',
          })}
          className="att-home-page-delivery-area"
        >
          <LocationOutlined size={36} color="textSecondary" />
          <Shape>
            <Caption color="textSecondary">{t('Xem sản phẩm tại')}</Caption>
            <Body
              maxWidth="128px"
              line={1}
              level={2}
              weight="medium"
              color="textSecondary"
            >
              {locationName}
            </Body>
          </Shape>
          <DownOutlined size={24} color="textSecondary" />
        </NavItem>
      </RegionPickerSuggestionPopover>
    );
  };

  const ThreeLevelLocationPickerIcon = () => {
    return (
      <RegionPickerSuggestionPopover
        active={!!showRegionPickerSuggestionPopover}
        onClose={onCloseRegionPickerSuggestionPopover ?? (() => {})}
      >
        <NavItem
          onClick={onOpenThreeLevelLocationPopup}
          className="att-home-page-delivery-area"
        >
          <LocationOutlined size={36} color="textSecondary" />
          <Shape>
            <Caption color="textSecondary">{t('Xem sản phẩm tại')}</Caption>
            {!!correctThreeLevelLocation ? (
              <Body
                level={2}
                weight="medium"
                color="textSecondary"
                maxWidth="128px"
                line={1}
              >
                {correctThreeLevelLocation.ward.label},{' '}
                {correctThreeLevelLocation.district?.label},{' '}
                {correctThreeLevelLocation.province.label}
              </Body>
            ) : (
              <Body level={2} weight="medium" color="textSecondary">
                {t('Chưa có địa chỉ được chọn')}
              </Body>
            )}
          </Shape>
          <DownOutlined size={24} color="textSecondary" />
        </NavItem>
      </RegionPickerSuggestionPopover>
    );
  };

  const navItems = useMemo(() => {
    const { order, login, cart, additional } = defaultNavItemDisplayConfig;
    const navMenu = [];
    if (order)
      navMenu.push(
        <NavigationButton
          className="button"
          onClick={(): void => {
            if (!user?.userId) {
              onLogin();
            } else {
              router.push('/account/orders');
            }
          }}
          style={
            isVerticalNavItem(tenant)
              ? { flexDirection: 'column' }
              : { flexDirection: 'row' }
          }
          {...getTrackingData({
            isTrackContent: true,
            regionName: 'headerBar',
            contentName: 'orderIcon',
          })}
        >
          <OrderOutlined size={36} color="textSecondary" />
          <span className="title">{t('Đơn hàng')}</span>
        </NavigationButton>,
      );
    if (login)
      navMenu.push(
        <UserProfileHeader
          vertical={isVerticalNavItem(tenant)}
          customProfile={
            !!partner ? (
              <CustomLink href="/account/orders">
                <NavigationButton className="button">
                  <ChecklistOutlined size={36} color="textSecondary" />
                  <div>{t('Quản lý đơn hàng')}</div>
                </NavigationButton>
              </CustomLink>
            ) : undefined
          }
          profileLinks={profileLinks}
        />,
      );
    if (notificationAppId)
      navMenu.push(<NotificationOverview title={isVerticalNavItem(tenant)} />);
    if (cart)
      navMenu.push(
        <CartOverview
          showShippingFeeMiniCart={true}
          vertical={isVerticalNavItem(tenant)}
        />,
      );
    if (locationPickerEnabled) {
      navMenu.push(
        <RegionPickerIcon locationName={selectedLocation?.locationName} />,
      );
    } else if (
      regionPickerProps?.isShowLocationIcon &&
      region &&
      regionPickerProps?.terminals?.length > 1
    ) {
      navMenu.push(<RegionPickerIcon locationName={region?.label} />);
    }
    if (additional) navMenu.push(additional);
    if (!isDmsFlow && !!epicOmni1541) {
      navMenu.push(<ThreeLevelLocationPickerIcon />);
    }
    return navMenu;
  }, [
    epicOmni1541,
    isDmsFlow,
    locationPickerEnabled,
    notificationAppId,
    partner,
    profileLinks,
    region,
    selectedLocation?.locationName,
    tenant,
    user?.userId,
    correctThreeLevelLocation,
  ]);
  return (
    <Flex flexWrap="nowrap" gap="s16" align="center" justify="end">
      {navItems.map((item, index) => (
        <Col key={index}>{item}</Col>
      ))}
      {!flags?.epic_ecom_284 && <PopupRegionPicker {...regionPickerProps} />}
    </Flex>
  );
};
